import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'
import {
  SearchLg,
  FilterLines,
  Trash01,
  Plus,
  DownloadCloud01,
  UploadCloud01,
} from '@untitled-ui/icons-react'
import {
  MyButton,
  MyColumn,
  MyDataTable,
  MyConsentStatusChip,
  MyHorizontalTabV2,
  MyConfirmModal,
  MyFilterModal,
  MyTextField,
  myToaster,
} from '@interstellar-component'
import TruncatedText from '@src/components/TruncatedText'
import AuthImage from '@src/components/AuthImage'
import { useWorkforce } from '../Context'
import { WorkforceService } from '../service'

const CATEGORY_TABS = [
  { label: 'All category', value: 'All category' },
  { label: 'Employee', value: 'Employee' },
  { label: 'Candidate', value: 'Candidate' },
]

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function WorkforceTable() {
  const navigate = useNavigate()
  const {
    workforce,
    workforceRows,
    workforceMeta,
    sortField,
    sortOrder,
    handleWorkforceSort,
    handleWorkforceSelectionChange,
    selectedCategory,
    setSelectedCategory,
    handleCurrentSlider,
    setPage,
    setSearchTerm,
    setParams,
    getWorkforce,
  } = useWorkforce()

  const rows = workforceRows || []
  const selectedRows = rows.filter((d) => d.checked)
  const selectedCount = selectedRows.length

  const total = Number(workforceMeta?.total ?? rows.length) || 0
  const currentPage = Number(workforceMeta?.current_page ?? 1) || 1
  const perPage = Number(workforceMeta?.per_page ?? workforceMeta?.limit ?? 10) || 10
  const totalPages = Math.max(1, Number(workforceMeta?.total_page ?? 1) || 1)

  const itemCountLabel = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(total)

  const debouncedSearch = useMemo(
    () => debounce((value) => setSearchTerm(value), 500),
    [setSearchTerm]
  )

  useEffect(
    () => () => {
      debouncedSearch.cancel()
    },
    [debouncedSearch]
  )

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleConfirmDelete = async () => {
    const ids = selectedRows.map((r) => r.id).filter(Boolean)
    if (ids.length === 0) return
    setIsDeleting(true)
    try {
      await WorkforceService.deleteWorkforce(ids)
      myToaster({ status: 200, message: `${ids.length} employee(s) deleted.` })
      setDeleteConfirmOpen(false)
      await getWorkforce()
    } catch (e) {
      myToaster(e)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExport = () => {
    const url = WorkforceService.exportWorkforce({
      ...(selectedCategory && selectedCategory !== 'All category'
        ? { category: selectedCategory.toLowerCase() }
        : {}),
    })
    if (url) window.open(url, '_blank')?.focus()
  }

  const values = {
    data: rows,
    meta: {
      current_page: currentPage,
      per_page: perPage,
      total,
      total_page: totalPages,
      prev_page: currentPage > 1 ? currentPage - 1 : null,
      next_page: currentPage < totalPages ? currentPage + 1 : null,
    },
    loading: workforce.loading,
    checkedAll: rows.length > 0 && rows.every((d) => d.checked),
  }

  return (
    <>
      <MyConfirmModal
        negativeActionWord="Cancel"
        positiveActionWord="Delete"
        open={deleteConfirmOpen}
        title="Delete employees"
        message={`Are you sure you want to delete ${selectedCount} employee(s)? This action cannot be undone.`}
        icon={<Trash01 className="text-error/600" />}
        positiveButtonColor="error"
        positiveActionWord={isDeleting ? 'Deleting...' : 'Delete'}
        onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      <div className="flex flex-1 min-h-0 flex-col px-8 pb-8 pt-2">
        <div className="flex flex-1 min-h-0 flex-col overflow-hidden border border-gray/200 bg-white shadow-sm rounded-xl">
          <div className="flex flex-col justify-between gap-4 border-b border-gray/200 px-6 py-3 sm:flex-row sm:items-center bg-gray/25">
            <div className="flex items-center gap-3">
              <h3 className="text-[14px] font-semibold text-gray-900">Employee List</h3>
              <span className="rounded-full border border-gray-blue/200 bg-gray-blue/50 px-2 py-0.5 text-xs font-medium text-gray-blue/700">
                {itemCountLabel} item
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Delete button temporarily hidden — soft-deleting a workforce
                  orphans active enquiries (Sequelize paranoid scope filters
                  the workforce join, so the slider renders blank fields).
                  Re-enable once we either auto-cancel related enquiries or
                  denormalise the snapshot onto the enquiry row. */}
              {false && selectedCount > 0 ? (
                <MyButton
                  color="error"
                  variant="outlined"
                  size="md"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  <Trash01 className="h-5 w-5" />
                  <p className="text-sm-semibold">Delete</p>
                </MyButton>
              ) : null}
              <MyButton color="primary" variant="outlined" size="md" onClick={handleExport}>
                <DownloadCloud01 className="h-5 w-5" />
                <p className="text-sm-semibold">Export</p>
              </MyButton>
              <MyButton
                color="secondary"
                variant="outlined"
                size="md"
                onClick={() => handleCurrentSlider({ status: true, current: 'import-workforce' })}
              >
                <UploadCloud01 className="h-5 w-5" />
                <p className="text-sm-semibold">Import</p>
              </MyButton>
              <MyButton
                color="primary"
                variant="filled"
                size="md"
                onClick={() => handleCurrentSlider({ status: true, current: 'new-employee' })}
              >
                <Plus className="h-5 w-5" />
                <p className="text-sm-semibold">New employee</p>
              </MyButton>
            </div>
          </div>

          <div className="flex flex-col gap-4 border-b border-gray-200 p-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="w-full max-w-sm">
              <MyTextField
                id="input-search"
                placeholder="Search for employee"
                startAdornment={
                  <SearchLg className="size-5 text-gray-light/600" stroke="currentColor" />
                }
                focusColor="#14378E"
                 
                onChangeForm={(e) => debouncedSearch(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <MyFilterModal
                id="filter-workforce"
                currentFilters={workforce?.filter}
                onChange={(filter) => {
                  // MyFilterModal.submit does `onChange({ ...arr })`, which
                  // spreads the entries array into an object with numeric
                  // keys. Coerce back to a real array so the query builder's
                  // `Array.isArray(params.filter)` check passes — otherwise
                  // the filter is silently dropped from the request and the
                  // list returns unfiltered data.
                  const arr = Array.isArray(filter) ? filter : Object.values(filter || {})
                  setParams((prev) => ({ ...prev, filter: arr, page: 1 }))
                }}
                target={(open, handleClick) => (
                  <MyButton
                    color="gray"
                    size="sm"
                    variant="tertiary"
                    customClassname="text-gray-700"
                    onClick={handleClick}
                  >
                    <FilterLines className="h-4 w-4 text-gray-500" stroke="currentColor" />
                    Filters
                  </MyButton>
                )}
              />
              <MyHorizontalTabV2
                value={selectedCategory}
                onChange={setSelectedCategory}
                fitContent
                tabs={CATEGORY_TABS}
              />
            </div>
          </div>

          <div className="flex flex-1 min-h-0 flex-col">
          <MyDataTable
            values={values}
            selectionMode="multiple"
            onSelectionChange={handleWorkforceSelectionChange}
            currentSortFieldFromParams={sortField}
            currentSortOrderFromParams={sortOrder}
            onClick={(row) => navigate(`/workforce/employee/${row.id}`, { state: { row } })}
            paginator
            onChangePagination={setPage}
          >
            <MyColumn
              header="Name & Employee ID"
              field="full_name"
              onSort={handleWorkforceSort}
              body={(row) => (
                <div className="flex items-center gap-3 py-1">
                  {row.avatar_url ? (
                    <AuthImage
                      src={row.avatar_url}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                      <span className="text-sm font-medium">{row.full_name?.charAt(0) ?? '?'}</span>
                    </div>
                  )}
                  <div className="flex max-w-[260px] min-w-0 flex-col gap-0.5">
                    <TruncatedText
                      text={row.full_name}
                      className="text-sm font-medium text-brand/700"
                    />
                    <TruncatedText
                      text={row.workforce_code ?? row.code}
                      className="text-sm text-gray-500"
                    />
                  </div>
                </div>
              )}
            />
            <MyColumn
              header="Level"
              field="employment_level"
              body={(row) => (
                <div className="max-w-[160px] min-w-0">
                  <TruncatedText
                    text={row.employment_level?.name ?? '—'}
                    className="text-sm text-gray-600"
                  />
                </div>
              )}
            />
            <MyColumn
              header="Category"
              field="category"
              onSort={handleWorkforceSort}
              body={(row) => (
                <span className="text-sm text-gray-600 capitalize">{row.category ?? '—'}</span>
              )}
            />
            <MyColumn
              header="Company"
              field="company"
              body={(row) => (
                <div className="max-w-[200px] min-w-0">
                  <TruncatedText
                    text={row.company?.name ?? '—'}
                    className="text-sm text-gray-600"
                  />
                </div>
              )}
            />
            <MyColumn
              header="Consent Status"
              field="consent_status"
              onSort={handleWorkforceSort}
              body={(row) => <MyConsentStatusChip status={row.consent_status} />}
            />
            <MyColumn
              header="Consent expiry"
              field="consent_expiry_at"
              onSort={handleWorkforceSort}
              body={(row) => (
                <span className="text-sm text-gray-600">{formatDate(row.consent_expiry_at)}</span>
              )}
            />
          </MyDataTable>
          </div>
        </div>
      </div>
    </>
  )
}

export default WorkforceTable
